import { ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Menu, LogOut } from "lucide-react";
import { usePlatformAdmin } from "@/contexts/PlatformAdminContext";
import { useAuth } from "@/contexts/AuthContext";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { PlatformAdminNavigation } from "@/components/platform-admin/PlatformAdminNavigation";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { WebhookErrorAlert } from "@/components/whatsapp/WebhookErrorAlert";
import { useWebhookErrorDetection } from "@/hooks/useWebhookErrorDetection";

interface AppLayoutProps {
  children: ReactNode;
  hideNavigation?: boolean;
  fullWidth?: boolean;
}

function AppLayoutContent({ children, hideNavigation, fullWidth }: AppLayoutProps) {
  const { open, isMobile, toggleSidebar } = useSidebar();
  const { userProfile, tenantData, signOut } = useAuth();
  const { t } = useTranslation(["common", "tenantAdmin"]);

  // N-31: this used to be wired screen-by-screen (classic Conversations, then
  // Conversations V2), so whether a merchant found out their WhatsApp channel was down
  // depended on which screen they happened to have open. Hoisting it here makes it a
  // stripe of the application instead of a stripe of one screen -- every tenant view
  // that goes through this layout (dashboard, tenant-admin, billing, conversations, ...)
  // now shows the same alarm. `showToasts` stays off: this frame renders a persistent
  // alert already, so a toast on top of it would just be a second, noisier copy.
  const { status: webhookStatus, forceCheck: forceWebhookCheck, loading: webhookLoading } =
    useWebhookErrorDetection({ tenantId: tenantData?.id, pollInterval: 60000, showToasts: false });
  const showWebhookAlert = !hideNavigation && !webhookLoading && !!webhookStatus && !!tenantData?.id
    && (webhookStatus.authenticationFailing || !webhookStatus.isHealthy || webhookStatus.recentErrors.length > 0);

  const getUserInitials = (name?: string) => {
    if (!name) return "U";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className={`flex w-full bg-background ${fullWidth ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
      {!hideNavigation && <AppSidebar />}
      {/* min-w-0: without it this flex item refuses to shrink below its widest
          child (flexbox's automatic minimum size), so one wide table inflated the
          whole page instead of scrolling inside itself. That is the shared cause
          behind the mobile overflow of billing, context-stores, testing, payments
          and whatsapp-admin (A-20, A-46): measured live, adding this word alone
          brought all five to exactly 390px. */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header with trigger - visible when the sidebar is collapsed (desktop) or
            always on mobile, where the sidebar is a Sheet that starts closed and
            `open` never reflects that: without this, there is no control to open it.
            On mobile it also carries the business name and the account menu (N-07) --
            before this, the mobile top bar was a single icon and nothing else. */}
        {!hideNavigation && (isMobile || !open) && (
          <header className="h-14 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex h-full items-center gap-2 px-4">
              <button
                type="button"
                onClick={toggleSidebar}
                aria-label={t("tenantAdmin:mobileHeader.openMenu")}
                className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent"
              >
                <Menu className="h-5 w-5" />
              </button>
              {isMobile && (
                <span className="flex-1 truncate font-semibold text-foreground">
                  {tenantData?.business_name || t("tenantAdmin:mobileHeader.defaultBusinessName")}
                </span>
              )}
              {!isMobile && <div className="flex-1" />}
              {isMobile && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={t("tenantAdmin:mobileHeader.accountMenu")}
                      className="flex h-9 w-9 items-center justify-center rounded-full hover:opacity-80"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                          {getUserInitials(userProfile?.full_name)}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={signOut} className="text-destructive">
                      <LogOut className="mr-2 h-4 w-4" />
                      {t("common:signOut")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </header>
        )}

        {/* N-31: the channel-down alarm, hoisted from individual screens to the shared
            frame -- see the hook wiring above. */}
        {showWebhookAlert && (
          <div className="flex-shrink-0 border-b bg-background px-4 py-1">
            <WebhookErrorAlert
              status={webhookStatus}
              onRetry={forceWebhookCheck}
              isRetrying={webhookLoading}
              className="mb-0"
              canReconnect={userProfile?.role === "tenant_admin" || userProfile?.role === "platform_admin"}
              reconnectHref="/whatsapp-admin"
            />
          </div>
        )}

        {/* Main content area */}
        <main className={`flex-1 ${fullWidth ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          {fullWidth ? children : (
            <div className="container mx-auto px-4 py-4">
              {children}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export function AppLayout({ children, hideNavigation, fullWidth }: AppLayoutProps) {
  const { isPlatformAdmin, validationState, loading } = usePlatformAdmin();

  // Lock layout decision on first mount to prevent React error #426 (hydration mismatch)
  // When isPlatformAdmin changes during session (e.g., after JWT refresh),
  // switching between two different DOM structures causes text content mismatch error
  const layoutDecisionRef = useRef<'platform_admin' | 'regular' | null>(null);

  // Make layout decision once when loading completes, then never change it
  if (layoutDecisionRef.current === null && !loading) {
    layoutDecisionRef.current = (isPlatformAdmin && validationState === 'valid')
      ? 'platform_admin'
      : 'regular';
  }

  // CRITICAL: Show loading screen until we have a locked decision
  // This prevents rendering either layout before we know which one to use
  // Without this, we render SidebarProvider first (when ref=null), then switch
  // to PlatformAdminNavigation (after ref is set), causing React error #426
  if (layoutDecisionRef.current === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const usePlatformAdminLayout = layoutDecisionRef.current === 'platform_admin';

  const envLabel = import.meta.env.VITE_ENVIRONMENT;
  const showEnvBanner = envLabel && envLabel !== 'production';

  if (import.meta.env.DEV) {
    console.log('🎯 [AppLayout] Layout Decision:', {
      isPlatformAdmin, validationState, loading,
      lockedDecision: layoutDecisionRef.current,
      willUsePlatformAdminLayout: usePlatformAdminLayout,
      currentPath: window.location.pathname
    });
  }

  // Platform admins use their existing navigation system
  // Only show platform admin layout if explicitly validated as platform admin (on mount)
  if (usePlatformAdminLayout) {
    return (
      <SidebarProvider>
        {showEnvBanner && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 text-center text-xs font-semibold py-0.5">
            {envLabel?.toUpperCase()} ENVIRONMENT
          </div>
        )}
        <div className={`flex w-full bg-background ${fullWidth ? 'h-screen overflow-hidden' : 'min-h-screen'} ${showEnvBanner ? 'pt-5' : ''}`}>
          <PlatformAdminNavigation />
          <main className={`flex-1 ${fullWidth ? 'overflow-hidden' : 'overflow-y-auto'}`}>
            {fullWidth ? children : (
              <div className="container mx-auto px-4 py-4">
                {children}
              </div>
            )}
          </main>
        </div>
      </SidebarProvider>
    );
  }

  // Regular users get the new sidebar layout
  return (
    <SidebarProvider>
      {showEnvBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 text-center text-xs font-semibold py-0.5">
          {envLabel?.toUpperCase()} ENVIRONMENT
        </div>
      )}
      <AppLayoutContent hideNavigation={hideNavigation} fullWidth={fullWidth}>
        {children}
      </AppLayoutContent>
    </SidebarProvider>
  );
}